import React, { useEffect, useState } from 'react';
import BEM from '../../bem';
import { ReactComponent as CircledCheckMarkIcon } from '../../../common/images/circled-checkmark.svg';
import { ReactComponent as PlusIcon } from '../../../common/images/plus-lean.svg';
import DotsIndicator from '../DotsIndicator';
import TaskCard from './TaskCard';
import type { Task } from 'js-sdk/src/types/Tasks';
import useFilterTasks, { type TasksFilter } from 'common/hooks/useFilterTasks';
import { tasksSlice } from 'redux-stores/tasks/tasksSlice';
import { useAppDispatch, useAppSelector } from 'redux-stores';
import useInfiniteScrollSentinel from 'common/hooks/useInfiniteScrollSentinel';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';

type MobxProps = {
  openModal: (type: string, options: Record<string, unknown>) => void;
};

const { setSelectedTask } = tasksSlice.actions;

const classes = BEM.with('TasksListView');

function EmptyState() {
  return (
    <div className={classes('empty')} data-test-id="tasks-empty-state">
      <CircledCheckMarkIcon aria-hidden className={classes('empty-icon')} />
      <span className={classes('empty-text')}>No tasks found</span>
    </div>
  );
}

const TasksListView: React.FC<MobxProps> = ({ openModal }) => {
  const organizationId = useAppSelector((state) => state.session.currentOrganizationId);
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState<TasksFilter>('active');

  const {
    tasks: filtered,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useFilterTasks({
    filter,
    orgId: organizationId,
    skip: !organizationId,
  });

  useEffect(() => {
    return () => {
      dispatch(setSelectedTask(null));
    };
  }, [dispatch]);
  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore: fetchNextPage,
  });

  return (
    <div className={classes('')} data-test-id="tasks-list-view">
      <div className={classes('header')}>
        <div className={classes('header--row')}>
          <CircledCheckMarkIcon aria-hidden className={classes('header-icon')} />
          <h2 className={classes('title')}>Tasks</h2>
          <button
            className={classes('create-button')}
            data-test-id="tasks-create-task"
            type="button"
            aria-label="Create task"
            onClick={() => openModal('createTask', {})}
          >
            <PlusIcon aria-hidden />
          </button>
        </div>
        <div className={classes('toggle')} data-test-id="tasks-filter-toggle">
          <button
            className={classes('toggle-btn', { active: filter === 'active' })}
            onClick={() => setFilter('active')}
            type="button"
          >
            Active
          </button>
          <button
            className={classes('toggle-btn', { active: filter === 'completed' })}
            onClick={() => setFilter('completed')}
            type="button"
          >
            Completed
          </button>
        </div>
      </div>
      {isLoading ? (
        <div className={classes('loading')}>
          <DotsIndicator />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <div className={classes('list')}>
          {filtered.map((task: Task) => (
            <TaskCard key={task.id} organizationId={organizationId ?? ''} task={task} />
          ))}
          {hasNextPage && (
            <div className={classes('sentinel')} ref={sentinelRef}>
              {isFetchingNextPage && <DotsIndicator />}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default mobxInjectSelect<object, MobxProps>({
  modalStore: ['openModal'],
})(TasksListView);
